/**
 * The journal, readable by both parents (`core-api` §9).
 *
 * Every parent read of a child's data is written here before the request is
 * served. It is shown unfiltered — including who looked — because a trail only
 * one parent can read is not a counter-power, it is a formality.
 */

import { useEffect, useState } from "react";

import { call } from "@/api/client";
import { ApiError } from "@/api/errors";
import type { AuditEntry } from "@/api/schema";
import { Card, Empty, ErrorNote, Loading, Screen } from "@/ui/primitives";

/**
 * English codes come from the server; these are the words a parent reads.
 *
 * **Une action absente d'ici s'affiche telle quelle** — `child.urgent_verbatim.read`
 * sur l'écran d'un parent, sur une ligne qui parle précisément d'un moment
 * difficile de son enfant. La table avait sept lignes de retard le 3 septembre
 * 2026 ; elle est complétée en même temps que chaque nouvelle action.
 */
const ACTION_LABEL: Record<string, string> = {
  "child.dashboard.read": "a consulté le tableau de bord",
  "child.profile.read": "a consulté le profil",
  "child.summary.read": "a lu les comptes rendus",
  "child.journal.read": "a lu le journal du soir",
  "child.effort.read": "a consulté le travail fourni",
  "child.workbook.read": "a consulté les compétences et exercices travaillés",
  "child.classwork.read": "a lu ce que les professeurs ont renseigné",
  "child.notebook.read": "a lu le cahier de la semaine",
  "child.notebook.corrected": "a retiré une compétence reconnue dans le cahier de la semaine",
  "child.planned_work.read": "a consulté le travail qui sera proposé",
  "child.seen.read": "a consulté ce que le programme dit avoir été vu en classe",
  "child.seen.declared": "a déclaré des chapitres vus en classe",
  "child.seen.assisted": "a demandé au tuteur quels chapitres compter comme vus",
  "child.parascolaire.read": "a feuilleté les cahiers du parascolaire",
  "child.entrainement.composed": "a composé un entraînement dans un cahier",
  "child.entrainement.closed": "a clos un entraînement et l'a envoyé à l'enfant",
  "child.entrainement.printed": "a imprimé un entraînement ou son corrigé",
  "child.entrainement.judged": "a jugé un exercice d'un entraînement",
  "child.entrainement.chat": "a demandé au tuteur de relire une copie",
  "child.seance_a_deux": "s'est assis à côté de l'enfant pour un exercice",
  "child.sujet.given": "a donné un sujet entier",
  "child.sujet.withdrawn": "a retiré un sujet entier avant qu'il soit rendu",
  "child.sujet.judged": "a jugé un sujet entier",
  "child.sujet.printed": "a imprimé un sujet ou son corrigé",
  "child.sujet.timed": "a lancé ou arrêté le chronomètre d'un sujet",
  "child.sujet.sunday": "a changé ou proposé le sujet du dimanche",
  "child.work.read": "a consulté le travail du jour",
  "child.validation.read": "a ouvert la file de validation",
  "child.validation.done": "a validé un exercice",
  "child.alerts.read": "a consulté les signaux d'inquiétude",
  "child.urgent_verbatim.read": "a lu un message signalé comme urgent",
  "setting.changed": "a modifié un réglage",
  "child.export.requested": "a demandé un export",
  "child.deletion.requested": "a demandé la suppression",
  "child.deletion.cancelled": "a annulé la suppression",
  "device.enrolled": "a enrôlé un appareil",
  "device.revoked": "a révoqué un appareil",
  "session.revoked": "a fermé une session",
  "pin.unlocked": "a débloqué un code",
};

export function AuditScreen() {
  const [entries, setEntries] = useState<AuditEntry[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void (async () => {
      try {
        setEntries(await call("readAudit", { query: { limit: 200 } }));
      } catch (failure) {
        setError(failure instanceof ApiError ? failure.message : "Chargement impossible.");
      }
    })();
  }, []);

  if (error) return <ErrorNote>{error}</ErrorNote>;
  if (!entries) return <Loading />;

  return (
    <Screen title="Journal">
      <p className="notice">
        Les deux parents lisent ce journal, sans filtre. Chaque consultation des données d'un
        enfant y figure.
      </p>
      <Card>
        {entries.length === 0 ? (
          <Empty>Rien pour l'instant.</Empty>
        ) : (
          <ul className="list list--dense">
            {entries.map((entry) => (
              <li key={entry.id} className="list__item">
                <div>
                  <strong>{ACTION_LABEL[entry.action] ?? entry.action}</strong>
                  <p className="muted small">{new Date(entry.at).toLocaleString("fr-FR")}</p>
                </div>
              </li>
            ))}
          </ul>
        )}
      </Card>
    </Screen>
  );
}
